import React, { useMemo } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  legacyValuesToAssigneeSelection,
  buildAssigneeTransferOptions,
  normalizeAssigneeSelectionOnChange,
  selectionAllowsMultiple,
  assigneeSelectionLabels,
} from "../utils/assigneeTransferField";

export default function AssigneeTransferField({
  label = "Transferir para",
  values = {},
  /** Alias de `values` (compat com formulários mais novos). */
  value,
  /** Seleção controlada (ex.: handoff_target com fila). Quando presente, tem prioridade sobre values. */
  selection = null,
  users = [],
  agents = [],
  loading = false,
  disabled = false,
  helperText,
  errorText,
  required,
  includeQueue = false,
  includeReceptionist = false,
  allowQueue,
  allowChatbots: _allowChatbots,
  allowAgents: _allowAgents,
  allowHumans: _allowHumans,
  queueLabel = "Fila de atendimento humano",
  receptionistLabel = "Recepcionista (agente de IA)",
  shortcutsGroup = "Fila e atalhos",
  onApply,
  onChange,
}) {
  const theme = useTheme();
  const showQueue = includeQueue === true || allowQueue === true;
  const valuesResolved = value != null ? value : values;

  const options = useMemo(
    () =>
      buildAssigneeTransferOptions({
        users,
        agents,
        includeQueue: showQueue,
        includeReceptionist,
        queueLabel,
        receptionistLabel,
        shortcutsGroup,
      }),
    [
      users,
      agents,
      showQueue,
      includeReceptionist,
      queueLabel,
      receptionistLabel,
      shortcutsGroup,
    ]
  );

  const selected = useMemo(() => {
    if (Array.isArray(selection)) return selection.filter(Boolean);
    return legacyValuesToAssigneeSelection(valuesResolved, { users, agents });
  }, [selection, valuesResolved, users, agents]);

  const allowMultiple = selectionAllowsMultiple(selected);

  const handleChange = (_, nextRaw) => {
    const nextArr = allowMultiple
      ? Array.isArray(nextRaw)
        ? nextRaw
        : nextRaw
        ? [nextRaw]
        : []
      : nextRaw
      ? [nextRaw]
      : [];
    const normalized = normalizeAssigneeSelectionOnChange(selected, nextArr);
    const labels = assigneeSelectionLabels(normalized);
    onApply?.(normalized, labels);
    onChange?.(normalized, labels);
  };

  return (
    <Autocomplete
      disabled={disabled}
      multiple={allowMultiple}
      options={options}
      groupBy={(option) => option.group}
      getOptionLabel={(option) => option?.label || ""}
      isOptionEqualToValue={(a, b) =>
        a?.kind === b?.kind && String(a?.id ?? "") === String(b?.id ?? "")
      }
      value={allowMultiple ? selected : selected[0] ?? null}
      onChange={handleChange}
      loading={loading}
      filterSelectedOptions
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          margin="dense"
          label={label}
          required={required}
          error={!!errorText}
          helperText={errorText || helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          key={`${option.kind}-${option.id}`}
          style={{
            ...props.style,
            fontWeight:
              option.kind === "agent" || option.kind === "queue" || option.kind === "receptionist"
                ? 600
                : option.kind === "chatbot"
                ? 500
                : 400,
            color:
              option.kind === "agent"
                ? theme.palette.secondary.main
                : option.kind === "queue" || option.kind === "receptionist"
                ? theme.palette.primary.main
                : option.kind === "chatbot"
                ? theme.palette.warning?.main || "#ed6c02"
                : undefined,
          }}
        >
          {option.label}
        </li>
      )}
    />
  );
}
